import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Coffee, ArrowLeft, Loader2 } from "lucide-react";
import { resolveMedia } from "@/utils/mediaResolver";
import { toast } from "sonner";


/**
 * POSProductSelector - Lista de produtos e categorias para o POS
 * Responsabilidades:
 * - Navegação por categorias (grid de categorias primeiro)
 * - Busca de produtos
 * - Exibição de produtos com imagens
 * - Indicador de quantidade no carrinho
 * - Badge de produto personalizável
 */
const LANGS = [
  { id: "pt", flag: "🇵🇹", label: "PT" },
  { id: "en", flag: "🇬🇧", label: "EN" },
  { id: "es", flag: "🇪🇸", label: "ES" },
];

// Returns the product name in the requested language, falling back to the default name.
function getProductName(product, lang) {
  if (!lang || lang === "pt") return product.name;
  return product.translations?.[lang] || product.name;
}

export function POSProductSelector({
  categories,
  products,
  onSelectProduct,
  getCartItem,
  getCartTotalQty,
  loading = false,
  lang = "pt",
  onLangChange,
  authAxios,
  onProductsTranslated,
}) {
  const [viewMode, setViewMode] = useState("categories"); // "categories" ou "products"
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [translating, setTranslating] = useState(false);

  // Reset para categorias quando limpar busca
  useEffect(() => {
    if (!search && viewMode === "products" && activeCat === "all") {
      setViewMode("categories");
    }
  }, [search, viewMode, activeCat]);

  // Check if product is available
  const isAvailable = (p) =>
    (p.active === undefined || p.active === true) &&
    (p.for_sale === undefined || p.for_sale === true);

  // Filtrar produtos (show all, including unavailable — but NOT show_in_pos:false)
  const filteredProducts = React.useMemo(() => {
    // Only hide products explicitly set show_in_pos=false
    let result = products.filter(p => p.show_in_pos !== false);

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.translations?.[lang] || "").toLowerCase().includes(q) ||
        p.code?.toLowerCase().includes(q)
      );
    } else if (activeCat !== "all") {
      result = result.filter(p => p.category_id === activeCat);
    }

    return result;
  }, [products, search, activeCat, lang]);

  // Auto-translate all products via Claude backend
  const handleAutoTranslate = async () => {
    if (!authAxios) return;
    setTranslating(true);
    try {
      const res = await authAxios.post("/pos/menu/auto-translate", { langs: ["en", "es"] });
      const { translated, skipped } = res.data;
      toast.success(`Menu traduzido! ${translated} produtos traduzidos, ${skipped} já tinham tradução.`);
      if (onProductsTranslated) onProductsTranslated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao traduzir menu");
    }
    setTranslating(false);
  };

  // Does the current lang have any translations in the product list?
  const hasTranslations = lang === "pt" || products.some(p => p.translations?.[lang]);

  // Contar produtos disponíveis por categoria
  const getCategoryCount = (catId) => {
    return products.filter(p =>
      p.category_id === catId &&
      p.show_in_pos !== false &&
      isAvailable(p)
    ).length;
  };

  // Voltar para categorias
  const backToCategories = () => {
    setViewMode("categories");
    setActiveCat("all");
    setSearch("");
  };

  // Selecionar categoria
  const selectCategory = (catId) => {
    setActiveCat(catId);
    setViewMode("products");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Barra de busca + seletor de idioma */}
      <div className="p-3 bg-white border-b space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={lang === "en" ? "Search product..." : lang === "es" ? "Buscar producto..." : "Buscar produto..."}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (e.target.value) setViewMode("products");
            }}
            className="pl-9 bg-gray-50 border-gray-200"
            data-testid="pos-search"
          />
        </div>
        {onLangChange && (
          <div className="flex items-center gap-2 flex-wrap">
            {LANGS.map(l => (
              <button
                key={l.id}
                type="button"
                onClick={() => onLangChange(l.id)}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border transition-all ${
                  lang === l.id
                    ? "bg-emerald-500 text-white border-emerald-500 shadow"
                    : "bg-white text-gray-500 border-gray-200 hover:border-emerald-300"
                }`}
              >
                <span>{l.flag}</span>
                <span>{l.label}</span>
              </button>
            ))}
            {!hasTranslations && authAxios && (
              <button
                type="button"
                onClick={handleAutoTranslate}
                disabled={translating}
                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border border-blue-300 text-blue-600 bg-blue-50 hover:bg-blue-100 transition-all disabled:opacity-50"
              >
                {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>🤖</span>}
                <span>{translating ? "Traduzindo..." : "Traduzir menu"}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modo Categorias */}
      {viewMode === "categories" && !search && (
        <ScrollArea className="flex-1">
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {categories.filter(c => getCategoryCount(c.id) > 0).map(cat => {
              const coverImg = cat.image_url
                || products.find(p => p.category_id === cat.id && (p.pos_media_url || p.image_url))?.pos_media_url
                || products.find(p => p.category_id === cat.id && (p.image_url))?.image_url;
              return (
              <button
                key={cat.id}
                onClick={() => selectCategory(cat.id)}
                className="bg-white rounded-xl border-2 border-gray-100 p-4 text-left transition-all hover:shadow-lg hover:border-emerald-300 active:scale-95"
                data-testid={`pos-cat-${cat.id?.slice(-6)}`}
              >
                {coverImg ? (
                  <img
                    src={resolveMedia(coverImg)}
                    alt={cat.name}
                    className="w-full h-20 sm:h-24 object-cover rounded-lg mb-2"
                    onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                  />
                ) : null}
                <div
                  className="w-full h-20 sm:h-24 bg-gradient-to-br from-emerald-100 to-gray-100 rounded-lg mb-2 items-center justify-center"
                  style={{ display: coverImg ? "none" : "flex" }}
                >
                  <Coffee className="w-8 h-8 text-emerald-300" />
                </div>
                <p className="font-bold text-gray-800 text-sm sm:text-base truncate">{cat.name}</p>
                <p className="text-xs text-gray-500">{getCategoryCount(cat.id)} productos</p>
              </button>
              );
            })}
          </div>
        </ScrollArea>
      )}

      {/* Modo Produtos */}
      {(viewMode === "products" || search) && (
        <>
          {/* Header com voltar */}
          {!search && (
            <div className="px-3 py-2 bg-white border-b flex items-center gap-2">
              <button 
                onClick={backToCategories}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <span className="font-semibold text-gray-800">
                {categories.find(c => c.id === activeCat)?.name || "Todos"}
              </span>
              <span className="text-sm text-gray-500">
                ({filteredProducts.length} productos)
              </span>
            </div>
          )}

          {/* Grid de produtos */}
          <ScrollArea className="flex-1">
            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filteredProducts.map(product => {
                const cartQty = getCartTotalQty ? getCartTotalQty(product.id) : (getCartItem ? getCartItem(product.id)?.quantity : 0);
                const inCart = cartQty > 0 ? { quantity: cartQty } : null;
                const hasModifiers = product.modifier_group_ids?.length > 0;
                const available = isAvailable(product);

                return (
                  <button
                    key={product.id}
                    onClick={() => available ? onSelectProduct(product) : undefined}
                    disabled={!available}
                    className={`relative bg-white rounded-xl border-2 p-3 text-left transition-all ${
                      !available
                        ? "border-red-300 opacity-75 cursor-not-allowed"
                        : inCart
                          ? "border-emerald-500 shadow-emerald-200 shadow-lg hover:shadow-lg active:scale-95"
                          : "border-gray-100 hover:border-emerald-300 hover:shadow-lg active:scale-95"
                    }`}
                    data-testid={`pos-product-${product.id?.slice(-6)}`}
                  >
                    {/* Linha vermelha topo — produto indisponível */}
                    {!available && (
                      <div className="absolute top-0 left-0 right-0 h-1 bg-red-500 rounded-t-xl z-10" />
                    )}

                    {/* Badge indisponível */}
                    {!available && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow z-10">
                        INDISP.
                      </div>
                    )}

                    {/* Quantidade no carrinho */}
                    {inCart && available && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-lg z-10">
                        {inCart.quantity}
                      </div>
                    )}

                    {/* Badge personalizável */}
                    {hasModifiers && available && (
                      <div className="absolute top-2 left-2 bg-blue-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow z-10">
                        PERSONALIZABLE
                      </div>
                    )}

                    {/* Imagem do produto */}
                    {(product.pos_media_url || product.image_url) ? (
                      <img
                        src={resolveMedia(product.pos_media_url || product.image_url)}
                        alt={product.name}
                        className="w-full h-20 sm:h-24 md:h-28 object-cover rounded-lg mb-2"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-20 sm:h-24 md:h-28 bg-gradient-to-br from-emerald-50 to-gray-100 rounded-lg mb-2 flex items-center justify-center">
                        <Coffee className="w-8 h-8 text-emerald-300" />
                      </div>
                    )}

                    {/* Nome do produto */}
                    <p className={`text-sm font-semibold leading-tight line-clamp-2 ${!available ? "line-through text-red-400" : "text-gray-800"}`}>
                      {getProductName(product, lang)}
                    </p>

                    {/* Preço */}
                    <p className="text-lg font-bold text-emerald-600 mt-1">
                      €{product.price?.toFixed(2)}
                    </p>
                  </button>
                );
              })}

              {filteredProducts.length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-400">
                  <Coffee className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No se encontraron productos</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </>
      )}
    </div>
  );
}
